import React from 'react';
import { Progress } from 'reactstrap';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { getAllTenants } from '@s20/selectors/features/common.selectors';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { AppNavPaths, ApiRoutes, agGridConstants } from '../Common';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    createDataSource, createGridOptions, indexCellRenderer, LinkCellRenderer, TextFilterDefaults, DataGrid,
    EmployeeAccountStatusRenderer,
} from '../common/dataGrid/DataGrid';
import {
    FlexEndRow, PageHeading, PageWrap, SmallButton,
} from '../common/forms/FormElements';
import { Can } from '../Can';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';

class DelegateIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            selectedRow: null,
        };
        this.onAddDelegateClicked = this.onAddDelegateClicked.bind(this);
    }

    onAddDelegateClicked = () => this.context.history.push(`${AppNavPaths.DelegateNew}`);

    componentDidMount() {
        return this.populateState();
    }

    populateState = async () => {
        const [groups, dispatchCompanies] = await Promise.all([
            httpClient.js(ApiRoutes.auth.groups()),
            httpClient.js(ApiRoutes.company.dispatchCompanies()),
        ]);
        const { tenant } = { ...this.context };

        //should not show current tenant
        const tenantOptions = (this.props.tenants ?? []).filter(
            (x) => x.id !== tenant.id,
        ) ?? [];

        const gridOptions = createGridOptions(this);

        const initialListFilters = {
            groupId: 0,
            tenantId: 0,
            dispatchCompanyId: 0,
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
        };

        const groupFilterParams = {
            suppressFilterButton: true,
            options: groups,
            optionsLabel: 'groupName',
            optionsValue: 'id',
            initialFilterValue: initialListFilters.groupId,
        };

        const dispatchCompanyFilterParams = {
            suppressFilterButton: true,
            options: dispatchCompanies,
            optionsLabel: 'companyName',
            optionsValue: 'id',
            initialFilterValue: initialListFilters.dispatchCompanyId,
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                width: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'UserName',
                sortable: true,
                headerName: 'Username',
                field: 'userName',
                tooltipField: 'userName',
                flex: 0,
                filter: agGridConstants.columnFilterTypes.text,
                filterParams: TextFilterDefaults,
                sort: { direction: 'asc', priority: 0 },
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                floatingFilter: true,
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    to: (delegateId) => `${AppNavPaths.Delegate}/${delegateId}`,
                    nameField: 'userName',
                    idField: 'employeeId',
                    title: 'View this Employee',
                },
            },
            {
                colId: 'LastName',
                sortable: true,
                headerName: 'Last Name',
                field: 'lastName',
                tooltipField: 'lastName',
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'employeeAccountStatusRenderer',
                cellRendererParams: {
                    nameField: 'lastName',
                },
            },
            {
                colId: 'FirstName',
                sortable: true,
                headerName: 'First Name',
                field: 'firstName',
                tooltipField: 'firstName',
                sort: { direction: 'asc' },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'employeeAccountStatusRenderer',
                cellRendererParams: {
                    nameField: 'firstName',
                },
            },
            {
                colId: 'EmployeeUser.Employee.Company.TenantCompany.CompanyName',
                sortable: false,
                headerName: 'Tenant Name',
                field: 'tenantName',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: {
                    labelText: 'Filter by type',
                    options: tenantOptions,
                    optionsLabel: 'companyName',
                    optionsValue: 'id',
                },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                    options: tenantOptions,
                    optionsLabel: 'companyName',
                    optionsValue: 'id',
                },
            },
            {
                colId: 'Group',
                sortable: false,
                headerName: 'Groups',
                field: 'groupsFlat',
                tooltipField: 'groupsFlat',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: groupFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: groupFilterParams,
            },
            {
                colId: 'DispatchCompany.CompanyName',
                sortable: false,
                headerName: 'Dispatch Offices',
                field: 'expandedDispatchCompaniesFlat',
                tooltipField: 'expandedDispatchCompaniesFlat',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchCompanyFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchCompanyFilterParams,
            },
        ];

        const ds = createDataSource(ApiRoutes.delegate.search(), gridOptions);
        this.setState({
            loading: false,
            gridOptions,
            dataSource: ds,
        });
    };
    render() {
        if (this.state.loading) return (<Progress />);
        const { rowData, gridOptions } = this.state;
        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faUser}
                        className="mr-2 text-muted"
                    />
                    <span>Delegates</span>
                </PageHeading>
                <DataGridToolbar
                    entity="delegate"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                >
                    <Can
                        I="all"
                        a="multi_tenant"
                    >
                        <FlexEndRow>
                            <Can
                                I="add"
                                a="employee"
                            >
                                <SmallButton
                                    type="button"
                                    onClick={this.onAddDelegateClicked}
                                >
                                    <i className="fa fa-md mr-2 fa-user-plus" />
                                    {' '}
                                    Add Delegate
                                </SmallButton>
                            </Can>
                        </FlexEndRow>
                    </Can>
                </DataGridToolbar>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}

const mapStateToProps = (state) => ({
    tenants: getAllTenants(state),
});

export default connect(mapStateToProps)(DelegateIndex);